import { useEffect, useRef, useState } from 'preact/hooks';
import {
  ApiError,
  addSubjectPageItem,
  createRecord,
  getSubjectPageState,
  listAllRecordsByType,
  uploadPhoto,
  uploadRecordBlob,
  type RecordRow,
} from '../lib/api';
import { activeOnly } from '../lib/retired';
import { describeReadFailure, type ReadFailure } from '../lib/readFailure';
import { openRecordEditor } from '../lib/recordEditor';
import { RecordMedia } from '../components/RecordMedia';
import { AA } from '../lib/auditionSpread';
import './Materials.css';

export interface MaterialsPhotosProps {
  tenantId: string;
  /** ⚑ 2026-09-24 — which `aa_media` kind this screen keeps. `'headshot'` is
   * what the public page publishes (pages.ts assertSelectable refuses any
   * other kind); until this prop existed nothing on this pack could create
   * one, so no CallToWrap account could ever put a headshot on its page.
   * Default `'photo'` keeps the Photos section byte-for-byte as it was. */
  kind?: 'photo' | 'headshot';
}

const WORDS = {
  photo: { group: 'Photos', empty: 'No photos yet.', add: '+ Add photo', aria: 'Add photo file', failed: 'The photo did not upload.' },
  headshot: {
    group: 'Headshots',
    empty: 'No headshots yet.',
    add: '+ Add headshot',
    aria: 'Add headshot file',
    failed: 'The headshot did not upload.',
  },
} as const;

// F-something, 2026-09-20 — PHOTOGRAPHS GET A HOME. `aa_media` has always
// declared `kind: 'photo'` alongside headshot/reel/resume/self_tape/other
// (recordTypes.ts), and the demo seed filed 9 of them (portraits, an EPK
// montage, a collage) — but nothing in the product listed them: this
// pack's own Materials strip (AA_MATERIALS_SECTIONS) only ever offered
// Tapes and Resume, and HouseholdTapes.tsx (the "tape log") lists every
// `aa_media` kind together BY DESIGN — a photo opened there rendered as a
// player that could never play (TapeRecord.tsx's own fix, same evening).
// This screen is the honest door for a kind of media that has always been
// filed and never been shown.
//
// ⚠ READS aa_media THROUGH THE GENERIC RECORDS API (listAllRecordsByType +
// RecordMedia), the same door PerformerResume.tsx's Looks strip and
// HouseholdTapes.tsx's log already use — NEVER /api/actor/media. That route
// (actorMedia.ts) serves the `actor_media` record type, which belongs to
// the OLD, never-shipped `actor` vertical pack (packages/verticals/actor),
// not this deployment's `actor_assist` pack — confirmed by
// recordFilesRoute.test.ts's own header ("unknown record type
// 'actor_media', proven live 2026-08-23"). MaterialsHeadshots.tsx and
// MaterialsReels.tsx are that OLD pack's own Materials screens (reached
// only from `MATERIALS_SECTIONS`, which App.tsx renders only for a
// non-actor_assist build) — their grid/upload SHAPE is worth borrowing
// (materials__scroll / pgrid / lookcard / addmedia, all from Materials.css)
// but their DATA PATH is not: it 404s "unknown record type" on every
// actor_assist tenant that has ever tried it.
export function MaterialsPhotos({ tenantId, kind = 'photo' }: MaterialsPhotosProps) {
  const words = WORDS[kind];
  const [photos, setPhotos] = useState<RecordRow[] | null>(null);
  const [failure, setFailure] = useState<ReadFailure | null>(null);
  const [subjectId, setSubjectId] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let cancelled = false;
    void Promise.all([listAllRecordsByType(tenantId, AA.MEDIA), listAllRecordsByType(tenantId, AA.SUBJECT)])
      .then(([media, subjects]) => {
        if (cancelled) return;
        setPhotos(activeOnly(media).filter((r) => r.fields['kind'] === kind));
        // One account, at most one subject now (R5) — the same lookup
        // PerformerResume.tsx's own upload door uses.
        setSubjectId(subjects[0]?.id ?? null);
        setFailure(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setPhotos([]);
        setFailure(describeReadFailure(err));
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId, nonce, kind]);

  /** B9 §3 — a headshot uploaded here goes on the public page as a CHOSEN
   * item, in upload order (the first becomes the hero: pickHero reads
   * position 0). Atlas's recommendation, awaiting Huan's ruling — if he rules
   * "unticked", delete this call and nothing else changes; Your page's "On the
   * page" strip is where a person ticks and unticks either way. Non-blocking by
   * design: the upload already succeeded; a failure here only means the photo
   * is not pre-ticked, which Your page shows honestly because it always
   * re-reads the items from the server. */
  async function addToPage(performerId: string, mediaId: string): Promise<void> {
    try {
      const state = await getSubjectPageState(tenantId, performerId);
      const position = state.items.filter((i) => i.itemKind === 'headshot').length;
      await addSubjectPageItem(tenantId, performerId, { itemKind: 'headshot', recordId: mediaId, position });
    } catch {
      /* see header — Your page re-reads the truth */
    }
  }

  async function onFile(e: Event) {
    const input = e.currentTarget as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      if (subjectId) {
        const { mediaId } = await uploadPhoto(tenantId, subjectId, file, { kind });
        if (kind === 'headshot') void addToPage(subjectId, mediaId);
      } else {
        // No performer record yet (a household that has never claimed its
        // own career) — the photo is still worth keeping, and the resume
        // door's own account handles the "no subject yet" case the same
        // way: file it, name nobody, rather than refuse the photograph.
        const { ref } = await uploadRecordBlob(tenantId, file);
        await createRecord(tenantId, 'aa_media', { kind, original_file_ref: ref });
      }
      setNonce((n) => n + 1);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : words.failed);
    } finally {
      setUploading(false);
      input.value = '';
    }
  }

  return (
    <div class="materials__scroll">
      <div class="materials__grp">{words.group}</div>
      {photos === null ? (
        <div class="materials__empty">One moment…</div>
      ) : failure ? (
        <div class="materials__error">
          {failure.headline} {failure.detail}
        </div>
      ) : photos.length === 0 ? (
        <div class="materials__empty">{words.empty}</div>
      ) : (
        <div class="pgrid">
          {photos.map((m) => (
            <div key={m.id} class="lookcard">
              <button
                type="button"
                class="lookcard__art"
                onClick={() => openRecordEditor({ mode: 'edit', typeKey: AA.MEDIA, id: m.id })}
              >
                <RecordMedia
                  tenantId={tenantId}
                  recordId={m.id}
                  fieldName="original_file_ref"
                  alt={photoLabel(m)}
                  className="lookcard__img"
                />
              </button>
              <div class="lookcard__meta">
                <div class="lookcard__t">{photoLabel(m)}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div class="addmedia">
        <button type="button" class="addmedia__btn" disabled={uploading} onClick={() => fileRef.current?.click()}>
          {uploading ? 'Adding…' : words.add}
        </button>
        <input
          ref={fileRef}
          class="addmedia__input"
          type="file"
          accept="image/*"
          aria-label={words.aria}
          onChange={onFile}
        />
      </div>
      {error ? <div class="materials__error">{error}</div> : null}
    </div>
  );
}

function photoLabel(m: RecordRow): string {
  const label = m.fields['label'];
  return typeof label === 'string' && label.trim() ? label : 'Photo';
}
